/**
 * Waterfall diagram (PRD §5.8 S-06, v1.5).
 *
 * Industry-standard DAS operator visualisation. Every operator familiar
 * with DAS expects this view; its absence is conspicuous.
 *
 *   X-axis: distance along the corridor (one column per segment)
 *   Y-axis: time (newest at top, scrolls down as new buckets land)
 *   Colour: aggregated acoustic energy — green→yellow→orange→red bands
 *
 * Implementation: Canvas API (NOT SVG). 1,200 cells (60 rows × 20 cols
 * default) updating on every poll is too many DOM nodes; one canvas
 * draw call is ~5 ms even for that grid. Rationale matches BUILD §S-06
 * acceptance: "render 1,200 cells in under 50 ms".
 *
 * Click handling: each cell maps back to its (segment, time) coords.
 * On click, parent's `onCellClick(segmentId, bucketStart)` is fired —
 * Ops Center will hook this to load the corresponding incident if one
 * exists, or show a tooltip otherwise.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";

export interface WaterfallRow {
  t: string;
  energies: number[];
}

export interface WaterfallData {
  site: string;
  windowStart: string;
  windowEnd: string;
  bucketSeconds: number;
  segments: string[];
  rows: WaterfallRow[];
}

interface WaterfallDiagramProps {
  data: WaterfallData;
  /** Cell click handler — receives segmentId + bucket start ISO. */
  onCellClick?: (segmentId: string, bucketStartISO: string) => void;
  /** Cell width px. Default 18. */
  cellWidth?: number;
  /** Cell height px. Default 8. */
  cellHeight?: number;
}

/**
 * Map energy value → CSS colour. Bands per BUILD §S-06:
 *   0–1.5×    : green     (rgb 64, 158, 102)
 *   1.5–5×    : yellow    (rgb 230, 195, 54)
 *   5–10×     : orange    (rgb 230, 130, 50)
 *   10×+      : red       (rgb 220, 60, 60)
 *
 * v1.5 uses event-count as the "energy" proxy (see waterfall-aggregator
 * for rationale). Empty cell = baseline = green band.
 *
 * EXPORTED for unit tests + the Ops Center legend.
 */
export function energyColour(value: number): string {
  if (value < 1.5) return "rgb(20,40,30)"; // dark-floor green
  if (value < 5) return "rgb(230,195,54)";
  if (value < 10) return "rgb(230,130,50)";
  return "rgb(220,60,60)";
}

interface HoverInfo {
  segmentId: string;
  bucketStart: string;
  energy: number;
  x: number;
  y: number;
}

export function WaterfallDiagram({
  data,
  onCellClick,
  cellWidth = 18,
  cellHeight = 8,
}: WaterfallDiagramProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);

  const width = Math.max(1, data.segments.length) * cellWidth;
  const height = Math.max(1, data.rows.length) * cellHeight;

  // ── Render to canvas ────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.fillStyle = "#0a0e1a"; // dark slate-950 floor
    ctx.fillRect(0, 0, width, height);

    // Draw cells. Rows are sorted newest-first in the API, so the top
    // of the canvas is `now`; bottom is windowStart.
    for (let r = 0; r < data.rows.length; r++) {
      const row = data.rows[r]!;
      for (let c = 0; c < row.energies.length; c++) {
        const energy = row.energies[c] ?? 0;
        if (energy < 1.5) continue; // skip drawing baseline cells (faster)
        ctx.fillStyle = energyColour(energy);
        ctx.fillRect(c * cellWidth, r * cellHeight, cellWidth, cellHeight);
      }
    }
  }, [data, width, height, cellWidth, cellHeight]);

  // ── Pointer interactions ───────────────────────────────────────────
  const handleMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const c = Math.floor(x / cellWidth);
      const r = Math.floor(y / cellHeight);
      if (c < 0 || c >= data.segments.length) {
        setHover(null);
        return;
      }
      if (r < 0 || r >= data.rows.length) {
        setHover(null);
        return;
      }
      const row = data.rows[r]!;
      setHover({
        segmentId: data.segments[c]!,
        bucketStart: row.t,
        energy: row.energies[c] ?? 0,
        x: c * cellWidth + cellWidth / 2,
        y: r * cellHeight,
      });
    },
    [data, cellWidth, cellHeight],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = (e.target as HTMLCanvasElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const c = Math.floor(x / cellWidth);
      const r = Math.floor(y / cellHeight);
      if (c < 0 || c >= data.segments.length) return;
      if (r < 0 || r >= data.rows.length) return;
      const row = data.rows[r]!;
      onCellClick?.(data.segments[c]!, row.t);
    },
    [data, cellWidth, cellHeight, onCellClick],
  );

  if (data.segments.length === 0 || data.rows.length === 0) {
    return (
      <section
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-500"
        data-testid="waterfall-empty"
      >
        <div className="flex items-center gap-2 text-slate-300 mb-1">
          <Activity className="size-4 text-teal-400" />
          <span className="uppercase tracking-wide font-semibold">
            Acoustic waterfall
          </span>
        </div>
        Awaiting data — no segments or events in the requested window.
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden"
      data-testid="waterfall-diagram"
    >
      <header className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-800 text-xs">
        <div className="flex items-center gap-2 text-slate-300">
          <Activity className="size-3.5 text-teal-400" />
          <span className="uppercase tracking-wide font-semibold">
            Acoustic waterfall
          </span>
          <span className="text-slate-500 font-mono normal-case">
            {data.segments.length} segments · {data.rows.length} buckets ·{" "}
            {data.bucketSeconds}s/cell
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-wider">
          <span className="flex items-center gap-1">
            <span
              className="inline-block size-2.5 rounded-sm"
              style={{ background: energyColour(2) }}
            />
            mod
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block size-2.5 rounded-sm"
              style={{ background: energyColour(7) }}
            />
            high
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block size-2.5 rounded-sm"
              style={{ background: energyColour(15) }}
            />
            critical
          </span>
        </div>
      </header>
      <div
        ref={containerRef}
        className="relative overflow-auto bg-slate-950"
        style={{ maxHeight: 480 }}
      >
        <canvas
          ref={canvasRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
          onClick={handleClick}
          data-testid="waterfall-canvas"
          className="block cursor-pointer"
          aria-label={`Acoustic waterfall: ${data.segments.length} segments by ${data.rows.length} time buckets`}
        />
        {hover && (
          <div
            className="pointer-events-none absolute z-10 rounded bg-slate-900 border border-slate-700 px-2 py-1 text-[11px] text-slate-200 shadow-lg"
            style={{
              left: Math.min(hover.x + 6, width - 160),
              top: Math.max(0, hover.y - 36),
            }}
            data-testid="waterfall-tooltip"
          >
            <div className="font-mono">{hover.segmentId}</div>
            <div className="text-slate-400 font-mono text-[10px]">
              {new Date(hover.bucketStart).toLocaleTimeString()}
            </div>
            <div>
              energy <span className="font-mono">{hover.energy.toFixed(0)}</span>×
            </div>
          </div>
        )}
      </div>
      <footer className="flex items-center justify-between px-4 py-1.5 border-t border-slate-800 text-[10px] text-slate-500 font-mono">
        <span>{new Date(data.windowStart).toLocaleTimeString()}</span>
        <span>← {data.segments[0]} · {data.segments[data.segments.length - 1]} →</span>
        <span>{new Date(data.windowEnd).toLocaleTimeString()}</span>
      </footer>
    </section>
  );
}
